/** @fileoverview Calls the attachment endpoints: upload, process, status, list, toggle, delete. */

import type {
  AttachmentStatusResponse,
  ConversationFilesResponse,
  ProcessAttachmentResponse,
  CreateAttachmentResponse,
  SetAttachmentEnabledResponse,
} from '../types/attachments';
import { assistantConversationPath } from './chatApi';
import { json } from './platformAuth';

const attachmentsPath = (id: string) => `${assistantConversationPath(id)}/attachments`;
const attachmentPath = (id: string, attachmentId: string) =>
  `${attachmentsPath(id)}/${encodeURIComponent(attachmentId)}`;

/** Call 1: registers the file and returns a presigned URL to send its bytes to. */
export const createAttachment = (
  tenantId: string,
  id: string,
  file: { name: string; size: number; mediaType: string }
) =>
  json<CreateAttachmentResponse>(attachmentsPath(id), tenantId, {
    method: 'POST',
    body: JSON.stringify(file),
  });

/** Call 2: sends the bytes straight to S3, with exactly the signed headers and no Platform auth. */
export const putToStorage = async (
  upload: CreateAttachmentResponse['upload'],
  body: Blob,
  signal: AbortSignal
) => {
  const response = await fetch(upload.url, {
    method: upload.method,
    headers: upload.headers,
    body,
    signal,
    credentials: 'omit',
  });
  if (!response.ok) throw new Error(`Upload failed (${response.status})`);
};

/** Call 3: checks and parses the stored file. Retry is the same call. */
export const processAttachment = (tenantId: string, id: string, attachmentId: string) =>
  json<ProcessAttachmentResponse>(`${attachmentPath(id, attachmentId)}/process`, tenantId, {
    method: 'POST',
  });

export const deleteAttachment = (tenantId: string, id: string, attachmentId: string) =>
  json(attachmentPath(id, attachmentId), tenantId, { method: 'DELETE' });

export const getAttachmentStatus = (tenantId: string, id: string, attachmentId: string) =>
  json<AttachmentStatusResponse>(attachmentPath(id, attachmentId), tenantId, {
    cache: 'no-store',
  });

/** Files already sent in this conversation, with how the model sees each. */
export const listConversationFiles = (tenantId: string, id: string, signal?: AbortSignal) =>
  json<ConversationFilesResponse>(attachmentsPath(id), tenantId, { signal, cache: 'no-store' });

/** Switches a sent file on or off for the model without re-uploading it. */
export const setAttachmentEnabled = (
  tenantId: string,
  id: string,
  attachmentId: string,
  enabled: boolean
) =>
  json<SetAttachmentEnabledResponse>(attachmentPath(id, attachmentId), tenantId, {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  });
